import Cookies from 'js-cookie'
import queryString from 'query-string'
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

// Cookie expiration in days
const COOKIE_EXPIRY = 30

/**
 * Custom hook to capture and store UTM parameters
 * @param {Object} options - Configuration options
 * @param {number} options.cookieExpiry - Cookie expiration in days
 * @returns {Object} UTM tracking utilities and current UTM state
 */
export const useUTMTracking = ({ cookieExpiry = COOKIE_EXPIRY } = {}) => {
  const [utmParams, setUtmParams] = useState<any>({})
  const [cookiesSaved, setCookiesSaved] = useState<boolean>(false)
  const location = useLocation()

  // List of parameters to track
  const UTM_PARAMS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'gclid',
    'fbclid',
    'step',
    // Add any other campaign parameters you need to track
  ]

  // Format parameter name for GTM and cookies (e.g., utm_source → custom_source)
  const formatParamName = (param: any) => {
    if (param.startsWith('utm_')) {
      return `custom_${param.substring(4)}`
    } else if (param === 'gclid') {
      return 'custom_gclick'
    } else if (param === 'fbclid') {
      return 'custom_fbc'
    } else if (param === 'step') {
      return 'step'
    }
    return `custom_${param}`
  }

  // Read UTM parameters from URL
  const getUTMParamsFromURL = () => {
    const queryParams = queryString.parse(window.location.search) || {}

    const params: any = {}
    let hasParams = false

    UTM_PARAMS.forEach((param) => {
      if (queryParams[param]) {
        params[param] = queryParams[param]
        hasParams = true
      }
    })

    return { params, hasParams }
  }

  // Save UTM parameters to cookies
  const saveUTMParamsToCookies = (params: any) => {
    Object.keys(params).forEach((param) => {
      const formattedName = formatParamName(param)
      Cookies.set(formattedName, params[param], { expires: cookieExpiry })
    })

    validateCookieSaving(params)
  }

  // Read UTM parameters from cookies
  const getUTMParamsFromCookies = () => {
    const params: any = {}
    let hasParams = false

    UTM_PARAMS.forEach((param) => {
      const formattedName = formatParamName(param)
      const value = Cookies.get(formattedName)

      if (value) {
        params[param] = value
        hasParams = true
      }
    })

    return { params, hasParams }
  }

  // Format UTM parameters for GTM
  const formatUTMForGTM = (params: any) => {
    const gtmData: any = {}

    Object.keys(params).forEach((param) => {
      const formattedName = formatParamName(param)
      gtmData[formattedName] = params[param]
    })

    return gtmData
  }

  const validateCookieSaving = (params: any) => {
    let allSaved = true

    // Check that all parameters were saved correctly
    Object.keys(params).forEach((param) => {
      const formattedName = formatParamName(param)
      const savedValue = Cookies.get(formattedName)

      if (!savedValue || savedValue !== String(params[param])) {
        allSaved = false
        console.warn(
          `Failed to save ${formattedName} cookie or value mismatch. Expected: ${params[param]}, Got: ${savedValue}`,
        )
      }
    })

    setCookiesSaved(allSaved)
    return allSaved
  }

  // Initialize on component mount
  useEffect(() => {
    // First check URL for fresh parameters
    const { params: urlParams, hasParams: hasUrlParams } = getUTMParamsFromURL()

    // If URL has parameters, save them and update state
    if (hasUrlParams) {
      saveUTMParamsToCookies(urlParams)
      setUtmParams(urlParams)
    } else {
      // If no URL parameters, try to get from cookies
      const { params: cookieParams, hasParams: hasCookieParams } = getUTMParamsFromCookies()

      if (hasCookieParams) {
        setUtmParams(cookieParams)
      }
    }
  }, [])

  // Get all current UTM values in formatted structure for GTM and API requests
  const getFormattedUTMParams = () => {
    const formatted: any = {}

    Object.keys(utmParams).forEach((param) => {
      const formattedName = formatParamName(param)
      formatted[formattedName] = utmParams[param]
    })

    return formatted
  }

  return {
    // Raw UTM parameters (utm_source, utm_medium, etc.)
    utmParams,

    // Formatted UTM parameters (custom_source, custom_medium, etc.)
    formattedUTMParams: getFormattedUTMParams(),

    // Helper method to get formatted parameters
    getFormattedUTMParams,

    // Helper method to get GTM-ready event object
    getGTMEventData: (eventName = 'utm_parameters') => ({
      event: eventName,
      ...getFormattedUTMParams(),
    }),

    // Tracking status
    isTracking: Object.keys(utmParams).length > 0,
  }
}
